import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Surface, Button, Tabs, Skeleton } from '@heroui/react'
import Download from '~icons/solar/download-bold'
import FileText from '~icons/solar/document-text-bold'
import ImageIcon from '~icons/solar/gallery-bold'
import Video from '~icons/solar/videocamera-record-bold'
import ExternalLink from '~icons/solar/square-top-down-bold'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { queryKeys } from '../../lib/queryClient'
import { formatDate } from '../../lib/format'

interface PromoMaterial {
  id: string
  title: string
  description: string
  type: 'image' | 'video' | 'pdf' | 'document'
  fileUrl: string
  thumbnailUrl?: string
  fileSize: string
  uploadedAt: string
  category: 'banners' | 'social' | 'email' | 'videos' | 'documents'
}

async function fetchPromoMaterials(): Promise<PromoMaterial[]> {
  // RLS (promo_materials "Affiliates can view active promo materials")
  // already scopes this to is_active rows in the caller's own organization
  // -- the explicit is_active filter here is just for query clarity.
  const { data, error } = await supabase
    .from('promo_materials')
    .select('id, title, description, type, category, file_url, thumbnail_url, file_size, created_at')
    .eq('is_active', true)
    .order('created_at', { ascending: false })

  if (error) throw error

  return (data || []).map((m: any) => ({
    id: m.id,
    title: m.title,
    description: m.description || '',
    type: m.type,
    fileUrl: m.file_url,
    thumbnailUrl: m.thumbnail_url || undefined,
    fileSize: m.file_size,
    uploadedAt: m.created_at,
    category: m.category,
  }))
}

export default function PromoMaterials() {
  const { user } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [thumbnailErrors, setThumbnailErrors] = useState<Record<string, boolean>>({})

  const { data: materials = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.promoMaterials(user?.organizationId || ''),
    queryFn: fetchPromoMaterials,
    enabled: !!user?.organizationId,
  })

  const categories = [
    { value: 'all', label: 'All Materials' },
    { value: 'banners', label: 'Banners' },
    { value: 'social', label: 'Social Media' },
    { value: 'email', label: 'Email Templates' },
    { value: 'videos', label: 'Videos' },
    { value: 'documents', label: 'Documents' }
  ]

  const filteredMaterials = selectedCategory === 'all'
    ? materials
    : materials.filter(m => m.category === selectedCategory)

  const getFileIcon = (type: PromoMaterial['type']) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-8 w-8 text-accent" />
      case 'video':
        return <Video className="h-8 w-8 text-accent" />
      case 'pdf':
      case 'document':
        return <FileText className="h-8 w-8 text-accent" />
      default:
        return <FileText className="h-8 w-8 text-accent" />
    }
  }

  const handleDownload = (material: PromoMaterial) => {
    const link = document.createElement('a')
    link.href = material.fileUrl
    link.download = material.title
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handlePreview = (material: PromoMaterial) => {
    window.open(material.fileUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Promotional Materials</h2>
        <p className="mt-2 text-muted">
          Download high-quality marketing materials to promote your referral link
        </p>
      </div>

      {/* Category Filter */}
      <Tabs selectedKey={selectedCategory} onSelectionChange={(key) => setSelectedCategory(key as string)}>
        <Tabs.ListContainer>
          <Tabs.List aria-label="Material categories">
            {categories.map(category => (
              <Tabs.Tab key={category.value} id={category.value} className="gap-2 whitespace-nowrap">
                {category.label}
                <Tabs.Indicator />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>

      {/* Materials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          [1, 2, 3, 4, 5, 6].map((i) => (
            <Surface key={i} variant="default" className="rounded-xl overflow-hidden">
              <Skeleton className="h-48 w-full rounded-none" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4 rounded" />
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-1/2 rounded" />
                <div className="flex gap-2 pt-1">
                  <Skeleton className="h-9 flex-1 rounded-xl" />
                  <Skeleton className="h-9 flex-1 rounded-xl" />
                </div>
              </div>
            </Surface>
          ))
        ) : filteredMaterials.map(material => (
          <Surface
            key={material.id}
            variant="default"
            className="rounded-xl overflow-hidden"
          >
            {/* Thumbnail or Icon */}
            <div className="h-48 flex items-center justify-center p-4 bg-accent-soft">
              {material.thumbnailUrl && !thumbnailErrors[material.id] ? (
                <img
                  src={material.thumbnailUrl}
                  alt={material.title}
                  className="max-h-full max-w-full object-contain"
                  onError={() =>
                    setThumbnailErrors((prev) => ({ ...prev, [material.id]: true }))
                  }
                />
              ) : (
                getFileIcon(material.type)
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-semibold text-foreground mb-1 truncate" title={material.title}>{material.title}</h3>
              <p className="text-sm text-muted mb-3">{material.description}</p>

              {/* Meta Info */}
              <div className="flex items-center justify-between text-xs text-muted mb-4">
                <span className="px-2 py-1 bg-surface-secondary rounded">
                  {material.fileSize}
                </span>
                <span>{formatDate(material.uploadedAt)}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onPress={() => handlePreview(material)}
                >
                  <ExternalLink className="h-4 w-4" />
                  Preview
                </Button>
                <Button
                  className="flex-1"
                  onPress={() => handleDownload(material)}
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>
          </Surface>
        ))}
      </div>

      {/* Empty State */}
      {!loading && filteredMaterials.length === 0 && (
        <Surface variant="default" className="text-center py-12 rounded-xl">
          <FileText className="h-12 w-12 text-muted mx-auto mb-3" />
          <p className="text-foreground font-medium">No materials available</p>
          <p className="text-sm text-muted mt-1">
            Check back soon for new promotional materials
          </p>
        </Surface>
      )}

      {/* Tips Section */}
      <Surface variant="secondary" className="rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-3">
          How to Use Promo Materials
        </h3>
        <ul className="space-y-2 text-sm text-foreground">
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5 text-accent">•</span>
            <span>Download materials and customize them with your referral link</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5 text-accent">•</span>
            <span>Share on social media, blogs, or email newsletters</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5 text-accent">•</span>
            <span>Always include your unique referral code or QR code</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold mt-0.5 text-accent">•</span>
            <span>Review our affiliate guidelines for compliance requirements</span>
          </li>
        </ul>
      </Surface>
    </div>
  )
}
